import { api } from 'common';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import SaveIcon from '@mui/icons-material/Save';
import { useDispatch, useSelector } from 'react-redux';
import { useAlert } from 'components/alert/AlertContext';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import { ThemedInput } from 'components/input/input/ThemedInput';
import { Box, Grid, Paper, Typography, Switch, Button, Divider, Alert, AlertTitle, Chip, FormControlLabel, Slider, Container, Fade, Avatar, alpha, useTheme } from '@mui/material';
import { Savings as SavingsIcon, People as PeopleIcon, DirectionsCar as CarIcon, Calculate as CalculateIcon, Wallet as WalletIcon, Percent as PercentIcon, AttachMoney as AttachMoneyIcon } from '@mui/icons-material';

const CashbackSettings = () => {
    const theme = useTheme();
    const { editSettings } = api;
    const dispatch = useDispatch();
    const { t } = useTranslation();
    const { showAlert } = useAlert();
    const [loading, setLoading] = useState(false);
    const [driverEnabled, setDriverEnabled] = useState(false);
    const [driverPercentage, setDriverPercentage] = useState(0);
    const [passengerEnabled, setPassengerEnabled] = useState(false);
    const [passengerPercentage, setPassengerPercentage] = useState(0);
    const [previewValue, setPreviewValue] = useState<string>('100,00');
    const [minTransactionAmount, setMinTransactionAmount] = useState(0);
    const settings = useSelector((state) => state.settingsdata?.settings);
    const [driverMaxAmount, setDriverMaxAmount] = useState<string | undefined>();
    const [passengerMaxAmount, setPassengerMaxAmount] = useState<string | undefined>();

    const convertFormattedToNumber = (formattedValue: string | undefined): number => {
        if (!formattedValue || formattedValue === '') return 0;
        const cleanValue = formattedValue.replace(/\./g, '').replace(',', '.');
        const numberValue = parseFloat(cleanValue);
        return isNaN(numberValue) ? 0 : numberValue;
    };

    const convertNumberToFormatted = (value: number | undefined): string => {
        if (!value) return '';
        return value.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    };

    useEffect(() => {
        if (settings) {
            const passengerAmount = settings.passenger_cashback_max_amount;
            setPassengerMaxAmount(passengerAmount ? convertNumberToFormatted(passengerAmount) : '');
            setPassengerEnabled(settings.passenger_cashback_enabled || false);
            setPassengerPercentage(settings.passenger_cashback_percentage || 0);

            const driverAmount = settings.driver_cashback_max_amount;
            setDriverMaxAmount(driverAmount ? convertNumberToFormatted(driverAmount) : '');
            setDriverEnabled(settings.driver_cashback_enabled || false);
            setDriverPercentage(settings.driver_cashback_percentage || 0);
        }
    }, [settings]);

    const handlePassengerPercentageChange = (value: number) => {
        let roundedValue = Math.round(value * 10) / 10;
        roundedValue = Math.min(100, Math.max(0, roundedValue));
        setPassengerPercentage(roundedValue);
    };

    const handleDriverPercentageChange = (value: number) => {
        let roundedValue = Math.round(value * 10) / 10;
        roundedValue = Math.min(100, Math.max(0, roundedValue));
        setDriverPercentage(roundedValue);
    };

    const calculateCashback = (amount: string, percentage: number, maxAmount?: string) => {
        const amountNumber = convertFormattedToNumber(amount);
        let cashback = (amountNumber * percentage) / 100;
        const maxAmountNumber = maxAmount ? convertFormattedToNumber(maxAmount) : 0;
        if (maxAmountNumber && cashback > maxAmountNumber) {
            cashback = maxAmountNumber;
        }
        return cashback;
    };

    const handleSave = async () => {
        setLoading(true);
        try {
            const updatedSettings = {
                ...settings,
                driver_cashback_enabled: driverEnabled ?? false,
                driver_cashback_max_amount: convertFormattedToNumber(driverMaxAmount),
                driver_cashback_percentage: parseFloat((driverPercentage ?? 0).toFixed(1)),
                passenger_cashback_enabled: passengerEnabled ?? false,
                passenger_cashback_max_amount: convertFormattedToNumber(passengerMaxAmount),
                passenger_cashback_percentage: parseFloat((passengerPercentage ?? 0).toFixed(1)),
            };

            await dispatch(editSettings(updatedSettings));
            showAlert({
                type: "SUCCESS",
                title: t("success"),
                message: t("CASHBACK_SAVED_SUCCESSFULLY")
            });
        } catch (error) {
            console.error(error);
            showAlert({
                type: "ERROR",
                title: t("error"),
                message: JSON.stringify(error) ?? t("no_details_error")
            });
        } finally {
            setLoading(false);
        }
    };

    const handleReset = () => {
        setPreviewValue('100,00');
        setDriverPercentage(0);
        setDriverEnabled(false);
        setPassengerPercentage(0);
        setMinTransactionAmount(0);
        setPassengerEnabled(false);
        setDriverMaxAmount('');
        setPassengerMaxAmount('');
    };

    const passengerPreview = calculateCashback(previewValue, passengerPercentage, passengerMaxAmount);
    const driverPreview = calculateCashback(previewValue, driverPercentage, driverMaxAmount);

    return (
        <Container maxWidth="xl" sx={{ py: 2 }}>
            <Fade in timeout={500}>
                <Box>
                    <Paper
                        elevation={0}
                        sx={{ p: 3, mb: 4, bgcolor: theme.palette.primary.main, color: theme.palette.primary.contrastText, borderRadius: 3 }} >
                        <Box display="flex" alignItems="center" justifyContent="space-between" flexWrap="wrap">
                            <Box display="flex" alignItems="center" gap={2}>
                                <Avatar sx={{ bgcolor: alpha(theme.palette.primary.contrastText, 0.2), width: 56, height: 56 }}>
                                    <SavingsIcon sx={{ fontSize: 32, color: theme.palette.primary.contrastText }} />
                                </Avatar>
                                <Box>
                                    <Typography variant="h5" fontWeight="bold" color={theme.palette.primary.contrastText}>
                                        {t('CASHBACK_SYSTEM')}
                                    </Typography>
                                    <Typography variant="body2" color={alpha(theme.palette.primary.contrastText, 0.9)}>
                                        {t('CASHBACK_SYSTEM_DESCRIPTION')}
                                    </Typography>
                                </Box>
                            </Box>
                            <Box display="flex" gap={1} sx={{ mt: 2 }}>
                                <Chip
                                    icon={<PeopleIcon />}
                                    sx={{ borderWidth: 0, p: 1 }}
                                    label={`${passengerPercentage.toFixed(1)}% ${t('FOR_PASSENGERS')}`}
                                />
                                <Chip
                                    icon={<CarIcon />}
                                    sx={{ borderWidth: 0, p: 1 }}
                                    label={`${driverPercentage.toFixed(1)}% ${t('FOR_DRIVERS')}`}
                                />
                            </Box>
                        </Box>
                    </Paper>

                    <Grid container spacing={3}>
                        <Grid item xs={12} lg={7}>
                            <Paper sx={{ borderRadius: 3, p: 3 }}>
                                <Box mb={4}>
                                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                                        <Typography variant="h6" fontWeight="bold" display="flex" alignItems="center" gap={1}>
                                            <PeopleIcon color="primary" />
                                            {t('PASSENGER_CASHBACK')}
                                        </Typography>
                                        <FormControlLabel
                                            control={
                                                <Switch
                                                    color="success"
                                                    checked={passengerEnabled}
                                                    onChange={(e) => setPassengerEnabled(e.target.checked)}
                                                />
                                            }
                                            label={passengerEnabled ? t('ENABLED') : t('DISABLED')}
                                        />
                                    </Box>

                                    {passengerEnabled && (
                                        <Box pl={2}>
                                            <Grid container spacing={2}>
                                                <Grid item xs={12}>
                                                    <Typography gutterBottom color="text.secondary">
                                                        {t('CASHBACK_PERCENTAGE')}: {passengerPercentage.toFixed(1)}%
                                                    </Typography>
                                                    <Slider
                                                        min={0}
                                                        step={0.1}
                                                        max={100}
                                                        sx={{ mb: 2 }}
                                                        valueLabelDisplay="auto"
                                                        value={passengerPercentage}
                                                        onChange={(_, value) => handlePassengerPercentageChange(value as number)}
                                                        marks={[
                                                            { value: 0, label: '0%' },
                                                            { value: 25, label: '25%' },
                                                            { value: 50, label: '50%' },
                                                            { value: 75, label: '75%' },
                                                            { value: 100, label: '100%' }
                                                        ]}
                                                    />
                                                    <Box display="flex" alignItems="center" gap={2} mt={1}>
                                                        <ThemedInput
                                                            type="number"
                                                            value={passengerPercentage}
                                                            inputProps={{ step: 0.1, min: 0, max: 100 }}
                                                            onChangeText={(text) => {
                                                                let value = parseFloat(text);
                                                                if (isNaN(value)) value = 0;
                                                                handlePassengerPercentageChange(value);
                                                            }}
                                                        />
                                                        <Typography>%</Typography>
                                                    </Box>
                                                </Grid>
                                                <Grid item xs={12}>
                                                    <ThemedInput
                                                        fullWidth
                                                        minValue={0}
                                                        mask='currency'
                                                        maxValue={9999999}
                                                        showNumberControls
                                                        keyboardType='decimal'
                                                        value={passengerMaxAmount || ''}
                                                        label={t('OPTIONAL_LEAVE_EMPTY_FOR_NO_LIMIT')}
                                                        placeholder={t('MAXIMUM_AMOUNT_PER_TRANSACTION')}
                                                        icon={{ size: 24, color: 'border', type: 'material', name: "AttachMoney" }}
                                                        onChangeText={(text) => {
                                                            setPassengerMaxAmount(text);
                                                        }}
                                                    />
                                                </Grid>
                                            </Grid>
                                        </Box>
                                    )}
                                </Box>

                                <Divider sx={{ my: 3 }} />

                                <Box mb={4}>
                                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                                        <Typography variant="h6" fontWeight="bold" display="flex" alignItems="center" gap={1}>
                                            <CarIcon color="primary" />
                                            {t('DRIVER_CASHBACK')}
                                        </Typography>
                                        <FormControlLabel
                                            control={
                                                <Switch
                                                    color="success"
                                                    checked={driverEnabled}
                                                    onChange={(e) => setDriverEnabled(e.target.checked)}
                                                />
                                            }
                                            label={driverEnabled ? t('ENABLED') : t('DISABLED')}
                                        />
                                    </Box>

                                    {driverEnabled && (
                                        <Box pl={2}>
                                            <Grid container spacing={2}>
                                                <Grid item xs={12}>
                                                    <Typography gutterBottom color="text.secondary">
                                                        {t('CASHBACK_PERCENTAGE')}: {driverPercentage.toFixed(1)}%
                                                    </Typography>
                                                    <Slider
                                                        min={0}
                                                        step={0.1}
                                                        max={100}
                                                        sx={{ mb: 2 }}
                                                        valueLabelDisplay="auto"
                                                        value={driverPercentage}
                                                        onChange={(_, value) => handleDriverPercentageChange(value as number)}
                                                        marks={[
                                                            { value: 0, label: '0%' },
                                                            { value: 25, label: '25%' },
                                                            { value: 50, label: '50%' },
                                                            { value: 75, label: '75%' },
                                                            { value: 100, label: '100%' }
                                                        ]}
                                                    />
                                                    <Box display="flex" alignItems="center" gap={2} mt={1}>
                                                        <ThemedInput
                                                            type="number"
                                                            value={driverPercentage}
                                                            onChangeText={(text) => {
                                                                let value = parseFloat(text);
                                                                if (isNaN(value)) value = 0;
                                                                handleDriverPercentageChange(value);
                                                            }}
                                                            inputProps={{ step: 0.1, min: 0, max: 100 }}
                                                        />
                                                        <Typography>%</Typography>
                                                    </Box>
                                                </Grid>
                                                <Grid item xs={12}>
                                                    <ThemedInput
                                                        fullWidth
                                                        minValue={0}
                                                        mask='currency'
                                                        maxValue={9999999}
                                                        showNumberControls
                                                        keyboardType='decimal'
                                                        value={driverMaxAmount || ''}
                                                        label={t('OPTIONAL_LEAVE_EMPTY_FOR_NO_LIMIT')}
                                                        placeholder={t('MAXIMUM_AMOUNT_PER_TRANSACTION')}
                                                        icon={{ size: 24, color: 'border', type: 'material', name: "AttachMoney" }}
                                                        onChangeText={(text) => {
                                                            setDriverMaxAmount(text);
                                                        }}
                                                    />
                                                </Grid>
                                            </Grid>
                                        </Box>
                                    )}
                                </Box>

                                <Divider sx={{ my: 3 }} />
                            </Paper>
                        </Grid>

                        <Grid item xs={12} lg={5}>
                            <Paper sx={{ borderRadius: 3, p: 3, mb: 3 }}>
                                <Typography variant="h6" fontWeight="bold" display="flex" alignItems="center" gap={1} mb={2}>
                                    <CalculateIcon color="primary" />
                                    {t('CASHBACK_PREVIEW')}
                                </Typography>

                                <ThemedInput
                                    fullWidth
                                    minValue={0}
                                    mask='currency'
                                    maxValue={9999999}
                                    showNumberControls
                                    value={previewValue}
                                    keyboardType='decimal'
                                    placeholder={t('TRANSACTION_AMOUNT')}
                                    icon={{ size: 24, color: 'border', type: 'material', name: "AttachMoney" }}
                                    onChangeText={(text) => {
                                        setPreviewValue(text);
                                    }}
                                />

                                <Alert severity="success" sx={{ my: 2 }}>
                                    <AlertTitle>{t('PREVIEW_RESULT')}</AlertTitle>
                                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                                        <Typography variant="body2" display="flex" alignItems="center" gap={0.5}>
                                            <PeopleIcon fontSize="small" /> {t('PASSENGER_CASHBACK')}:
                                        </Typography>
                                        <Typography variant="body1" fontWeight="bold" color="success.main">
                                            {settings?.symbol} {passengerPreview.toFixed(2)}
                                        </Typography>
                                    </Box>
                                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                                        <Typography variant="body2" display="flex" alignItems="center" gap={0.5}>
                                            <CarIcon fontSize="small" /> {t('DRIVER_CASHBACK')}:
                                        </Typography>
                                        <Typography variant="body1" fontWeight="bold" color="success.main">
                                            {settings?.symbol} {driverPreview.toFixed(2)}
                                        </Typography>
                                    </Box>
                                </Alert>

                                {minTransactionAmount > 0 && convertFormattedToNumber(previewValue) < minTransactionAmount && (
                                    <Alert severity="warning">
                                        {t('TRANSACTION_BELOW_MINIMUM', { amount: minTransactionAmount })}
                                    </Alert>
                                )}
                            </Paper>

                            <Paper sx={{ borderRadius: 3, p: 3 }}>
                                <Typography variant="h6" fontWeight="bold" display="flex" alignItems="center" gap={1} mb={2}>
                                    <WalletIcon color="primary" />
                                    {t('HOW_CASHBACK_WORKS')}
                                </Typography>

                                <Box display="flex" alignItems="flex-start" gap={1.5} mb={2}>
                                    <PercentIcon color="primary" fontSize="small" sx={{ mt: 0.3 }} />
                                    <Box>
                                        <Typography variant="subtitle2" fontWeight="bold">
                                            {t('PERCENTAGE_BASED')}
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary">
                                            {t('CASHBACK_PERCENTAGE_EXPLANATION')}
                                        </Typography>
                                    </Box>
                                </Box>

                                <Box display="flex" alignItems="flex-start" gap={1.5} mb={2}>
                                    <AttachMoneyIcon color="primary" fontSize="small" sx={{ mt: 0.3 }} />
                                    <Box>
                                        <Typography variant="subtitle2" fontWeight="bold">
                                            {t('MAXIMUM_LIMITS')}
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary">
                                            {t('CASHBACK_LIMIT_EXPLANATION')}
                                        </Typography>
                                    </Box>
                                </Box>
                            </Paper>
                        </Grid>
                    </Grid>

                    <Box display="flex" justifyContent="center" gap={2} padding={5}>
                        <Button
                            size='large'
                            color="secondary"
                            variant="outlined"
                            onClick={handleReset}
                            startIcon={<RestartAltIcon />}
                        >
                            {t('reset')}
                        </Button>
                        <Button
                            size='large'
                            color="primary"
                            disabled={loading}
                            variant="contained"
                            onClick={handleSave}
                            startIcon={<SaveIcon />}
                        >
                            {loading ? t('saving') : t('save')}
                        </Button>
                    </Box>
                </Box>
            </Fade>
        </Container>
    );
};

export default CashbackSettings;